<template>
  <div class="product-card-type-d">
    <div
      class="product-card-type-d__image-wrapper"
      @click="emit('product-click', product)"
      @keydown.enter="emit('product-click', product)"
      @keydown.space="emit('product-click', product)"
      role="button"
      tabindex="0"
      :aria-label="'Product: ' + product.dt"
    >
      <ImageField
        class="product-card-type-d__image"
        :obj="{ url: product.iu, alt: product.dt, width: '608', height: '760' }"
        :isLoadingLazy="true"
        role="img"
      />

      <!-- Wishlist icon -->
      <button
        v-if="showWishlistIcon"
        class="product-card-type-d__wishlist-icon-cta"
        @click.stop="emit('remove-from-wishlist', product)"
        aria-label="Remove from wishlist"
        :aria-pressed="true"
      >
        <svg aria-hidden="true">
          <use xlink:href="#wishlist-selected-icon"></use>
        </svg>
      </button>
    </div>

    <div class="product-card-type-d__desc">
      <div class="product-card-type-d__desc-details">
        <div class="product-card-type-d__desc-details-title" role="heading" aria-level="2">{{ product.dt }}</div>
        <div class="product-card-type-d__desc-details-price">{{ formatPrice(getPrice(product)) }}</div>
        <div class="product-card-type-d__desc-details-selected-options">
          <div
            v-for="(value, key) in getOptions(product)"
            :key="key"
            class="product-card-type-d__desc-details-selected-option"
          >
            {{ key }}: {{ value }}
          </div>
        </div>
      </div>

      <div class="product-card-type-d__desc-add-to-cart-cta">
        <button
          v-if="available"
          class="btn btn--secondary btn--medium"
          data-add-to-cart
          @click="emit('add-to-cart', product)"
          :disabled="isATCDisabled(product)"
        >
          ADD TO CART
        </button>

        <button v-else-if="!available && !isBis" data-add-to-cart disabled class="btn btn--secondary btn--medium">OUT OF STOCK</button>
        <button v-else class="btn btn--secondary btn--medium" @click="bisDrawer">
          Notify Me When Available
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
  /** ProductCardTypeD is used in Wishlist pages like Wislist listing page, Share page */
  // This component currently accepts different props inside product object which comes from Wishlist API
  import { computed } from 'vue';
import ImageField from '../image.vue';
  import productCardProps from './product-card-props';

  const props = defineProps({
    product: productCardProps.product,
    showWishlistIcon: productCardProps.showWishlistIcon,
    available: productCardProps.available,
  });

  const emit = defineEmits(['product-click', 'remove-from-wishlist', 'add-to-cart']);

  const getOptions = (product) => {
    const { productDetails, variantDetails } = product?.cprops;
    const { options } = productDetails;
    const generatedOptions = {};

    for (let option in options) {
      if (variantDetails[options[option].name]) {
        generatedOptions[options[option].name] = variantDetails[options[option].name];
      }
    }

    return generatedOptions;
  };

  const isBis = computed(() => {
    const tags = props?.product?.cprops?.productDetails?.tags || [];
    const selectedOptions = getOptions(props.product);
    // Get the first option key
    const key = selectedOptions ? Object.keys(selectedOptions)[0] : '';

    return !props.available && tags.filter(tag => tag.toLowerCase().includes(`bis: ${key.toLowerCase()}`)).length > 0;
  });

  const bisDrawer = () => {
    window.toggleDrawer(true, 'BISModal', {
      productId: props.product.cprops.productDetails.id,
      variantId: props.product.cprops.variantDetails.selectedVariant.id,
      bisDate: isBis.value && isBis.value[0].split('[')[1].replace(']','')
    });
  }; 

  const formatPrice = (value) => {
    return window.formattedPrice(value);
  };

  const getCustomerDiscount = (productTags) => {
    return window.customerDiscount(productTags);
  };

  const getPrice = (product) => {
    const { productDetails, variantDetails } = product?.cprops;
    const customerDiscountVal = getCustomerDiscount(productDetails.tags);

    if (variantDetails.selectedVariant.compare_at_price > 0) {
      const { compare_at_price, price } = variantDetails.selectedVariant;

      if (compare_at_price * (1 - customerDiscountVal) < price) {
        return (compare_at_price * (1 - customerDiscountVal)) / 100;
      } else {
        return price / 100;
      }
    } else {
      return (variantDetails.selectedVariant.price / 100) * (1 - customerDiscountVal);
    }
  };

  const isATCDisabled = (product) => {
    const { id, inventory_quantity } = product?.cprops?.variantDetails?.selectedVariant;
    let flag = false;

    if (id && cartItems.value?.length) {
      const selCartItem = cartItems.value.find((item) => item.id == id);
      flag = selCartItem?.quantity === inventory_quantity;
    }

    return flag;
  };
</script>
